import React, { useState } from 'react';
import GridRow from './GridRow';

function Canvas({
                    grid,
                    setGrid,
                    draggedItem,
                    setDraggedItem,
                    selectedElement,
                    setSelectedElement,
                    selectedCell,
                    setSelectedCell,
                    selectedRow,
                    setSelectedRow,
                    showNotification,
                    containerWidth,
                    setContainerWidth,
                    previewMode,
                    previewMember,
                    demoAssets,
                    onDetachTemplate
                }) {
    const [draggedExistingElement, setDraggedExistingElement] = useState(null);
    const [showContainerSettings, setShowContainerSettings] = useState(false);

    // Gérer les liens en mode preview
    React.useEffect(() => {
        if (previewMode) {
            // En mode preview, ajouter target="_blank" à tous les liens
            const canvasArea = document.querySelector('.canvas-area.preview-mode');
            if (canvasArea) {
                const links = canvasArea.querySelectorAll('a');
                links.forEach(link => {
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                });
            }
        }
    }, [previewMode, grid]);

    // Recalculer les largeurs des cellules quand containerWidth change
    React.useEffect(() => {
        if (grid.length === 0) return;

        const newGrid = grid.map(row => {
            const cells = row.cells;
            if (!cells || cells.length === 0) return row;

            // Calculer la largeur totale actuelle
            const getCurrentWidth = (cell) => {
                const width = cell?.style?.width;
                if (!width || width === 'auto') return 0;
                return parseInt(width);
            };

            const currentTotalWidth = cells.reduce((sum, cell) => sum + getCurrentWidth(cell), 0);

            // Si toutes les cellules sont en auto ou si la somme est 0, ne rien faire
            if (currentTotalWidth === 0) return row;

            // Calculer le ratio de redimensionnement
            const ratio = containerWidth / currentTotalWidth;

            // Appliquer le ratio à chaque cellule avec respect du minimum
            const MIN_WIDTH = 50;
            const newCells = cells.map(cell => {
                const currentWidth = getCurrentWidth(cell);
                if (currentWidth === 0) return cell;

                const newWidth = Math.max(MIN_WIDTH, Math.round(currentWidth * ratio));
                return {
                    ...cell,
                    style: {
                        ...cell.style,
                        width: `${newWidth}px`
                    }
                };
            });

            // Vérifier que la somme des nouvelles largeurs ne dépasse pas containerWidth
            const newTotalWidth = newCells.reduce((sum, cell) => {
                const width = cell.style?.width;
                return sum + (width ? parseInt(width) : 0);
            }, 0);

            // Si on dépasse à cause des arrondis ou du MIN_WIDTH, ajuster la dernière cellule
            if (newTotalWidth !== containerWidth && newCells.length > 0) {
                const diff = containerWidth - newTotalWidth;
                const lastCell = newCells[newCells.length - 1];
                const lastCellWidth = parseInt(lastCell.style.width);
                const adjustedWidth = Math.max(MIN_WIDTH, lastCellWidth + diff);

                newCells[newCells.length - 1] = {
                    ...lastCell,
                    style: {
                        ...lastCell.style,
                        width: `${adjustedWidth}px`
                    }
                };
            }

            return {
                ...row,
                cells: newCells
            };
        });

        setGrid(newGrid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerWidth]); // Uniquement quand containerWidth change

    // Nettoyer les classes drag-over lors du clic
    React.useEffect(() => {
        const handleClick = () => {
            document.querySelectorAll('.drag-over').forEach(el => {
                el.classList.remove('drag-over');
            });
        };

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    React.useEffect(() => {
        const handleNestedDrop = (e) => {
            const { parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex } = e.detail;

            if (!draggedItem || draggedItem.type === 'structure') return;

            const newGrid = [...grid];
            const parentCell = newGrid[parentRowIndex].cells[parentCellIndex];
            const nestedCell = parentCell.nestedGrid[nestedRowIndex].cells[nestedCellIndex];

            const newElement = {
                id: Date.now(),
                type: draggedItem.type,
                value: draggedItem.defaultValue || '',
                style: { ...(draggedItem.defaultStyle || {}) }
            };

            if (draggedItem.type === 'button' || draggedItem.type === 'banner' || draggedItem.type === 'icontext') {
                newElement.link = draggedItem.link || '#';
            }
            if (draggedItem.type === 'social') {
                newElement.network = draggedItem.network || 'linkedin';
            }
            if (draggedItem.type === 'socialgroup') {
                newElement.networks = draggedItem.networks || ['linkedin', 'twitter', 'facebook'];
                newElement.links = draggedItem.links || {};
            }
            if (draggedItem.type === 'icontext') {
                newElement.icon = draggedItem.iconValue || '📧';
                newElement.text = draggedItem.textValue || 'Texte';
            }
            if (draggedItem.type === 'qrcode') {
                newElement.qrData = draggedItem.defaultValue || 'https://example.com';
            }

            if (!nestedCell.elements) {
                nestedCell.elements = [];
            }
            nestedCell.elements.push(newElement);

            setGrid(newGrid);
            setDraggedItem(null);
            setSelectedElement(newElement);
            showNotification('Élément ajouté dans la grille imbriquée', 'success');
        };

        document.addEventListener('nestedCellDrop', handleNestedDrop);
        return () => document.removeEventListener('nestedCellDrop', handleNestedDrop);
    }, [draggedItem, grid, setGrid, setDraggedItem, showNotification]);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDropOnCell = (e, rowIndex, cellIndex) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedItem) return;

        const newGrid = [...grid];

        if (draggedItem.type === 'structure') {
            const targetCell = newGrid[rowIndex].cells[cellIndex];
            if (!targetCell.nestedGrid) {
                targetCell.nestedGrid = [];
            }
            targetCell.nestedGrid.push({
                cells: Array(draggedItem.columns).fill(null).map(() => ({
                    elements: [],
                    style: {
                        padding: '10px',
                        verticalAlign: 'top',
                        width: `${Math.floor(100 / draggedItem.columns)}%`
                    }
                }))
            });
        } else {
            const newElement = {
                id: Date.now(),
                type: draggedItem.type,
                value: draggedItem.defaultValue || '',
                style: { ...(draggedItem.defaultStyle || {}) }
            };

            if (draggedItem.type === 'button' || draggedItem.type === 'banner' || draggedItem.type === 'icontext') {
                newElement.link = draggedItem.link || '#';
            }
            if (draggedItem.type === 'social') {
                newElement.network = draggedItem.network || 'linkedin';
            }
            if (draggedItem.type === 'socialgroup') {
                newElement.networks = draggedItem.networks || ['linkedin', 'twitter', 'facebook'];
                newElement.links = draggedItem.links || {};
            }
            if (draggedItem.type === 'icontext') {
                newElement.icon = draggedItem.iconValue || '📧';
                newElement.text = draggedItem.textValue || 'Texte';
            }
            if (draggedItem.type === 'qrcode') {
                newElement.qrData = draggedItem.defaultValue || 'https://example.com';
            }

            newGrid[rowIndex].cells[cellIndex].elements.push(newElement);
            // Sélectionner automatiquement le nouvel élément
            setSelectedElement(newElement);
        }

        setGrid(newGrid);
        setDraggedItem(null);
        showNotification('Élément ajouté', 'success');
    };

    const handleDropNewRow = (e, position) => {
        e.preventDefault();
        e.stopPropagation();

        if (!draggedItem || draggedItem.type !== 'structure') return;

        const newRow = {
            cells: Array(draggedItem.columns).fill(null).map(() => {
                const cellWidth = Math.floor(containerWidth / draggedItem.columns);

                return {
                    elements: [],
                    style: {
                        padding: '15px',
                        verticalAlign: 'top',
                        width: `${cellWidth}px`
                    }
                };
            })
        };

        const newGrid = [...grid];
        newGrid.splice(position, 0, newRow);
        setGrid(newGrid);
        setDraggedItem(null);
        showNotification('Ligne ajoutée', 'success');
    };

    const moveRow = (index, direction) => {
        const newGrid = [...grid];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= newGrid.length) return;

        [newGrid[index], newGrid[newIndex]] = [newGrid[newIndex], newGrid[index]];
        setGrid(newGrid);

        // Mettre à jour la sélection de la ligne si elle est déplacée
        if (selectedRow === index) {
            setSelectedRow(newIndex);
        } else if (selectedRow === newIndex) {
            setSelectedRow(index);
        }

        // Mettre à jour la sélection de cellule si elle est affectée
        if (selectedCell && !selectedCell.isNested) {
            if (selectedCell.rowIndex === index) {
                setSelectedCell({ ...selectedCell, rowIndex: newIndex });
            } else if (selectedCell.rowIndex === newIndex) {
                setSelectedCell({ ...selectedCell, rowIndex: index });
            }
        } else if (selectedCell && selectedCell.isNested) {
            if (selectedCell.parentRowIndex === index) {
                setSelectedCell({ ...selectedCell, parentRowIndex: newIndex });
            } else if (selectedCell.parentRowIndex === newIndex) {
                setSelectedCell({ ...selectedCell, parentRowIndex: index });
            }
        }

        showNotification('Ligne déplacée', 'success');
    };

    const deleteRow = (index) => {
        const newGrid = grid.filter((_, i) => i !== index);
        setGrid(newGrid);
        if (selectedRow === index) setSelectedRow(null);
        showNotification('Ligne supprimée', 'success');
    };

    const addNestedGrid = (rowIndex, cellIndex) => {
        const newGrid = [...grid];
        const cell = newGrid[rowIndex].cells[cellIndex];

        if (!cell.nestedGrid) {
            cell.nestedGrid = [];
        }

        cell.nestedGrid.push({
            cells: [{ elements: [], style: { padding: '10px', verticalAlign: 'top', width: '100%' } }]
        });

        setGrid(newGrid);
        showNotification('Grille imbriquée ajoutée', 'success');
    };

    const clearCell = (rowIndex, cellIndex) => {
        const newGrid = [...grid];
        newGrid[rowIndex].cells[cellIndex].elements = [];
        newGrid[rowIndex].cells[cellIndex].nestedGrid = undefined;
        setGrid(newGrid);
        showNotification('Cellule vidée', 'success');
    };

    const clearNestedCell = (parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex) => {
        const newGrid = [...grid];
        const parentCell = newGrid[parentRowIndex].cells[parentCellIndex];

        if (parentCell.nestedGrid && parentCell.nestedGrid[nestedRowIndex]) {
            parentCell.nestedGrid[nestedRowIndex].cells[nestedCellIndex].elements = [];
            parentCell.nestedGrid[nestedRowIndex].cells[nestedCellIndex].nestedGrid = undefined;
        }

        setGrid(newGrid);
        showNotification('Cellule imbriquée vidée', 'success');
    };

    // NOUVEAU: Ajouter une colonne à une ligne
    const addColumn = (rowIndex) => {
        const newGrid = [...grid];
        const row = newGrid[rowIndex];

        if (row.cells.length >= 4) {
            showNotification('Maximum 4 colonnes atteint', 'error');
            return;
        }

        row.cells.push({
            elements: [],
            style: { padding: '15px', verticalAlign: 'top' }
        });

        // Recalculer les largeurs en pixels pour toutes les colonnes
        const newColumnCount = row.cells.length;
        const widthPx = Math.floor(containerWidth / newColumnCount);

        row.cells.forEach(cell => {
            if (!cell.style) cell.style = {};
            cell.style.width = `${widthPx}px`;
        });

        setGrid(newGrid);
        showNotification('Colonne ajoutée', 'success');
    };

    // NOUVEAU: Supprimer une colonne
    const deleteColumn = (rowIndex, cellIndex) => {
        const newGrid = [...grid];
        const row = newGrid[rowIndex];

        if (row.cells.length === 1) {
            // Si c'est la dernière colonne, supprimer toute la ligne
            newGrid.splice(rowIndex, 1);
            showNotification('Dernière colonne supprimée, ligne supprimée', 'success');
        } else {
            row.cells.splice(cellIndex, 1);

            // Recalculer les largeurs en pixels pour les colonnes restantes
            const newColumnCount = row.cells.length;
            const widthPx = Math.floor(containerWidth / newColumnCount);

            row.cells.forEach(cell => {
                if (!cell.style) cell.style = {};
                cell.style.width = `${widthPx}px`;
            });

            showNotification('Colonne supprimée', 'success');
        }

        setGrid(newGrid);
        if (selectedCell?.rowIndex === rowIndex && selectedCell?.cellIndex === cellIndex) {
            setSelectedCell(null);
        }
    };

    // NOUVEAU: Déplacer une colonne
    const moveColumn = (rowIndex, cellIndex, direction) => {
        const newGrid = [...grid];
        const row = newGrid[rowIndex];
        const newIndex = direction === 'left' ? cellIndex - 1 : cellIndex + 1;

        if (newIndex < 0 || newIndex >= row.cells.length) return;

        [row.cells[cellIndex], row.cells[newIndex]] = [row.cells[newIndex], row.cells[cellIndex]];

        setGrid(newGrid);
        showNotification('Colonne déplacée', 'success');
    };

    // NOUVEAU: Supprimer une colonne imbriquée
    const deleteNestedColumn = (parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex) => {
        const newGrid = [...grid];
        const parentCell = newGrid[parentRowIndex].cells[parentCellIndex];
        const nestedRow = parentCell.nestedGrid[nestedRowIndex];

        if (nestedRow.cells.length === 1) {
            // Si c'est la dernière colonne, supprimer toute la grille imbriquée
            parentCell.nestedGrid.splice(nestedRowIndex, 1);
            if (parentCell.nestedGrid.length === 0) {
                delete parentCell.nestedGrid;
            }
            showNotification('Dernière colonne imbriquée supprimée, grille supprimée', 'success');
        } else {
            nestedRow.cells.splice(nestedCellIndex, 1);

            // Recalculer les pourcentages pour les colonnes imbriquées restantes
            const newColumnCount = nestedRow.cells.length;
            const widthPercent = Math.floor(100 / newColumnCount);
            nestedRow.cells.forEach(cell => {
                if (!cell.style) cell.style = {};
                cell.style.width = `${widthPercent}%`;
            });

            showNotification('Colonne imbriquée supprimée', 'success');
        }

        setGrid(newGrid);
        if (selectedCell?.isNested &&
            selectedCell?.parentRowIndex === parentRowIndex &&
            selectedCell?.parentCellIndex === parentCellIndex &&
            selectedCell?.nestedRowIndex === nestedRowIndex &&
            selectedCell?.nestedCellIndex === nestedCellIndex) {
            setSelectedCell(null);
        }
    };

    // NOUVEAU: Déplacer une colonne imbriquée
    const moveNestedColumn = (parentRowIndex, parentCellIndex, nestedRowIndex, nestedCellIndex, direction) => {
        const newGrid = [...grid];
        const parentCell = newGrid[parentRowIndex].cells[parentCellIndex];
        const nestedRow = parentCell.nestedGrid[nestedRowIndex];
        const newIndex = direction === 'left' ? nestedCellIndex - 1 : nestedCellIndex + 1;

        if (newIndex < 0 || newIndex >= nestedRow.cells.length) return;

        [nestedRow.cells[nestedCellIndex], nestedRow.cells[newIndex]] = [nestedRow.cells[newIndex], nestedRow.cells[nestedCellIndex]];

        setGrid(newGrid);
        showNotification('Colonne imbriquée déplacée', 'success');
    };

    // NOUVEAU: Ajouter une colonne à une grille imbriquée
    const addNestedColumn = (parentRowIndex, parentCellIndex, nestedRowIndex) => {
        const newGrid = [...grid];
        const parentCell = newGrid[parentRowIndex].cells[parentCellIndex];
        const nestedRow = parentCell.nestedGrid[nestedRowIndex];

        if (nestedRow.cells.length >= 4) {
            showNotification('Maximum 4 colonnes atteint', 'error');
            return;
        }

        nestedRow.cells.push({
            elements: [],
            style: { padding: '10px', verticalAlign: 'top' }
        });

        // Recalculer les pourcentages pour toutes les colonnes imbriquées
        const newColumnCount = nestedRow.cells.length;
        const widthPercent = Math.floor(100 / newColumnCount);
        nestedRow.cells.forEach(cell => {
            if (!cell.style) cell.style = {};
            cell.style.width = `${widthPercent}%`;
        });

        setGrid(newGrid);
        showNotification('Colonne imbriquée ajoutée', 'success');
    };

    const deleteElement = (elementId) => {
        const newGrid = [...grid];

        const removeElement = (grid) => {
            for (const row of grid) {
                for (const cell of row.cells) {
                    const index = cell.elements?.findIndex(el => el.id === elementId);
                    if (index !== -1) {
                        cell.elements.splice(index, 1);
                        return true;
                    }
                    if (cell.nestedGrid && removeElement(cell.nestedGrid)) {
                        return true;
                    }
                }
            }
            return false;
        };

        removeElement(newGrid);
        setGrid(newGrid);
        if (selectedElement?.id === elementId) {
            setSelectedElement(null);
        }
        showNotification('Élément supprimé', 'success');
    };

    // Déplacer un élément vers le haut ou le bas dans une cellule
    const moveElement = (rowIndex, cellIndex, elementIndex, direction, nestedRowIndex = null, nestedCellIndex = null) => {
        const newGrid = [...grid];

        let elements;
        if (nestedRowIndex !== null && nestedCellIndex !== null) {
            elements = newGrid[rowIndex].cells[cellIndex].nestedGrid[nestedRowIndex].cells[nestedCellIndex].elements;
        } else {
            elements = newGrid[rowIndex].cells[cellIndex].elements;
        }

        if (!elements || elements.length < 2) return;

        const newIndex = direction === 'up' ? elementIndex - 1 : elementIndex + 1;
        if (newIndex < 0 || newIndex >= elements.length) return;

        // Échanger les éléments
        [elements[elementIndex], elements[newIndex]] = [elements[newIndex], elements[elementIndex]];

        setGrid(newGrid);
    };

    const handleCellResize = (rowIndex, cellIndex, newWidthPx) => {
        const newGrid = [...grid];
        const row = newGrid[rowIndex];
        const cells = row.cells;
        const totalColumns = cells.length;

        // Si c'est la dernière cellule, on ne peut pas redimensionner (pas de cellule à droite)
        if (cellIndex >= totalColumns - 1) return;

        // Récupérer les largeurs actuelles
        const getCurrentWidth = (cell) => {
            const width = cell.style?.width;
            if (!width) return containerWidth / totalColumns;
            return parseInt(width);
        };

        const currentWidth = getCurrentWidth(cells[cellIndex]);
        const nextCellWidth = getCurrentWidth(cells[cellIndex + 1]);

        // Calculer la différence
        const diff = newWidthPx - currentWidth;

        // Largeur minimale par cellule
        const MIN_WIDTH = 50;

        // Vérifier que la nouvelle largeur ne dépasse pas les limites
        if (newWidthPx < MIN_WIDTH) return;

        // Vérifier que la cellule suivante ne sera pas trop petite
        const newNextCellWidth = nextCellWidth - diff;
        if (newNextCellWidth < MIN_WIDTH) return;

        // Vérifier que la somme ne dépasse pas la largeur du container
        const otherCellsWidth = cells.reduce((sum, cell, idx) => {
            if (idx !== cellIndex && idx !== cellIndex + 1) {
                return sum + getCurrentWidth(cell);
            }
            return sum;
        }, 0);

        if (newWidthPx + newNextCellWidth + otherCellsWidth > containerWidth) return;

        // Appliquer les nouvelles largeurs
        if (!cells[cellIndex].style) {
            cells[cellIndex].style = {};
        }
        if (!cells[cellIndex + 1].style) {
            cells[cellIndex + 1].style = {};
        }

        cells[cellIndex].style.width = `${newWidthPx}px`;
        cells[cellIndex + 1].style.width = `${newNextCellWidth}px`;

        setGrid(newGrid);
    };

    const handleElementDragStart = (dragData) => {
        setDraggedExistingElement(dragData);
    };

    const handleElementDrop = (dragData, targetRowIndex, targetCellIndex, targetNestedRowIndex = null, targetNestedCellIndex = null) => {
        const { element, sourceRowIndex, sourceCellIndex, sourceNestedRowIndex, sourceNestedCellIndex } = dragData;

        if (sourceRowIndex === targetRowIndex && sourceCellIndex === targetCellIndex &&
            sourceNestedRowIndex === targetNestedRowIndex && sourceNestedCellIndex === targetNestedCellIndex) {
            return;
        }

        const newGrid = [...grid];

        const removeElementFromSource = () => {
            if (sourceNestedRowIndex !== null && sourceNestedCellIndex !== null) {
                const sourceParentCell = newGrid[sourceRowIndex].cells[sourceCellIndex];
                const sourceNestedCell = sourceParentCell.nestedGrid[sourceNestedRowIndex].cells[sourceNestedCellIndex];
                const elementIndex = sourceNestedCell.elements.findIndex(el => el.id === element.id);
                if (elementIndex !== -1) {
                    sourceNestedCell.elements.splice(elementIndex, 1);
                }
            } else {
                const sourceCell = newGrid[sourceRowIndex].cells[sourceCellIndex];
                const elementIndex = sourceCell.elements.findIndex(el => el.id === element.id);
                if (elementIndex !== -1) {
                    sourceCell.elements.splice(elementIndex, 1);
                }
            }
        };

        const addElementToTarget = () => {
            if (targetNestedRowIndex !== null && targetNestedCellIndex !== null) {
                const targetParentCell = newGrid[targetRowIndex].cells[targetCellIndex];
                const targetNestedCell = targetParentCell.nestedGrid[targetNestedRowIndex].cells[targetNestedCellIndex];
                if (!targetNestedCell.elements) {
                    targetNestedCell.elements = [];
                }
                targetNestedCell.elements.push(element);
            } else {
                const targetCell = newGrid[targetRowIndex].cells[targetCellIndex];
                if (!targetCell.elements) {
                    targetCell.elements = [];
                }
                targetCell.elements.push(element);
            }
        };

        removeElementFromSource();
        addElementToTarget();

        setGrid(newGrid);
        setDraggedExistingElement(null);
        showNotification('Élément déplacé', 'success');
    };

    if (grid.length === 0) {
        return (
            <div className="canvas-area">
                <div className="canvas-container">
                    <div
                        className="canvas-empty-state"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDropNewRow(e, 0)}
                        style={{
                            minHeight: '400px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: draggedItem ? '2px dashed #3498db' : '2px dashed transparent',
                            borderRadius: '8px',
                            transition: 'all 0.2s'
                        }}
                    >
                        <div className="canvas-empty-state-icon">📧</div>
                        <h2>Commencez votre signature</h2>
                        <p>Glissez une structure depuis la gauche pour commencer</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`canvas-area ${previewMode ? 'preview-mode' : ''}`} style={{ position: 'relative' }}>
            {/* Bouton de paramètres du container */}
            {!previewMode && (
                <button
                    className="container-settings-btn"
                    onClick={() => setShowContainerSettings(true)}
                    title="Paramètres du container"
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        zIndex: 1000
                    }}
                >
                    ⚙️
                </button>
            )}

            {/* Modal de paramètres */}
            {showContainerSettings && (
                <div className="modal-overlay" onClick={() => setShowContainerSettings(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h2>⚙️ Paramètres du container</h2>
                        <div className="form-group">
                            <label>Largeur du container (px)</label>
                            <input
                                type="number"
                                min="500"
                                max="650"
                                value={containerWidth}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value) || 600;
                                    // Clamper entre 500 et 650
                                    const clampedValue = Math.max(500, Math.min(650, value));
                                    setContainerWidth(clampedValue);
                                }}
                                onBlur={(e) => {
                                    // S'assurer que la valeur est bien dans les limites au blur
                                    const value = parseInt(e.target.value);
                                    if (value < 500) {
                                        setContainerWidth(500);
                                        e.target.value = 500;
                                    } else if (value > 650) {
                                        setContainerWidth(650);
                                        e.target.value = 650;
                                    }
                                }}
                            />
                            <small style={{ fontSize: '11px', color: '#7f8c8d', display: 'block', marginTop: '4px' }}>
                                Recommandé : 600px (min: 500px, max: 650px)
                            </small>
                        </div>
                        <div className="modal-actions">
                            <button onClick={() => setShowContainerSettings(false)} className="btn-primary">
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="canvas-container" style={{ minWidth: `${containerWidth + 80}px` }}>

                {/* Wrapper centré pour la grid */}
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <div style={{ width: `${containerWidth + 4}px` }}>
                        {!previewMode && (
                            <div
                                style={{
                                    height: draggedItem?.type === 'structure' ? '20px' : '0px',
                                    background: draggedItem?.type === 'structure' ? '#3498db' : 'transparent',
                                    margin: draggedItem?.type === 'structure' ? '8px 0' : '0',
                                    borderRadius: '4px',
                                    transition: 'all 0.2s'
                                }}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDropNewRow(e, 0)}
                            />
                        )}

                        {grid.map((row, rowIndex) => (
                            <React.Fragment key={rowIndex}>
                                <GridRow
                                    row={row}
                                    rowIndex={rowIndex}
                                    totalRows={grid.length}
                                    selectedRow={selectedRow}
                                    selectedCell={selectedCell}
                                    selectedElement={selectedElement}
                                    containerWidth={containerWidth}
                                    onSelectRow={setSelectedRow}
                                    onSelectCell={setSelectedCell}
                                    onSelectElement={setSelectedElement}
                                    onSelectNestedCell={setSelectedCell}
                                    onMoveRow={moveRow}
                                    onDeleteRow={deleteRow}
                                    onAddNestedGrid={addNestedGrid}
                                    onClearCell={clearCell}
                                    onClearNestedCell={clearNestedCell}
                                    onDeleteElement={deleteElement}
                                    onDrop={handleDropOnCell}
                                    onDragOver={handleDragOver}
                                    onCellResize={handleCellResize}
                                    onElementDragStart={handleElementDragStart}
                                    onElementDrop={handleElementDrop}
                                    onAddColumn={addColumn}
                                    onDeleteColumn={deleteColumn}
                                    onMoveColumn={moveColumn}
                                    onDeleteNestedColumn={deleteNestedColumn}
                                    onMoveNestedColumn={moveNestedColumn}
                                    onAddNestedColumn={addNestedColumn}
                                    onMoveElement={moveElement}
                                    previewMode={previewMode}
                                    previewMember={previewMember}
                                    demoAssets={demoAssets}
                                    onDetachTemplate={onDetachTemplate}
                                />

                                {!previewMode && (
                                    <div
                                        style={{
                                            height: draggedItem?.type === 'structure' ? '20px' : '0px',
                                            background: draggedItem?.type === 'structure' ? '#3498db' : 'transparent',
                                            margin: draggedItem?.type === 'structure' ? '8px 0' : '0',
                                            borderRadius: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDropNewRow(e, rowIndex + 1)}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Canvas;
